import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Constants } from '../helpers/constants';
import { environment } from '../../../environments/environment';

type InstanceKey = 'ffm' | 'prspr' | 'cpjp';

function normalizeInstance(raw: string | null | undefined): InstanceKey | null {
  const v = (raw || '').toLowerCase().trim();
  if (v === 'ffm' || v === 'prspr' || v === 'cpjp') return v;
  return null;
}

function originFromEnv(url: string | undefined): string | null {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed, window.location.href).origin;
  } catch {
    return null;
  }
}

function inferInstance(): InstanceKey | null {
  try {
    const instParam = normalizeInstance(new URL(window.location.href).searchParams.get('inst'));
    if (instParam) return instParam;
  } catch {
    // ignore
  }

  switch (window.location.port) {
    case '4202':
      return 'prspr';
    case '4204':
      return 'cpjp';
    case '4200':
      return 'ffm';
  }

  const currentOrigin = window.location.origin;
  const prsprOrigin = originFromEnv(environment.PrsprFrontendUrl);
  if (prsprOrigin && prsprOrigin === currentOrigin) return 'prspr';

  const cpjpOrigin = originFromEnv(environment.cpjpFrontendUrl);
  if (cpjpOrigin && cpjpOrigin === currentOrigin) return 'cpjp';

  const ffmOrigin = originFromEnv(environment.ffmFrontendUrl);
  if (ffmOrigin && ffmOrigin === currentOrigin) return 'ffm';

  return null;
}

export class InstanceTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private prefix: string = 'assets/i18n/',
    private suffix: string = '.json'
  ) {}

  getTranslation(lang: string): Observable<any> {
    const basePath = `${this.prefix}${lang}${this.suffix}`;

    const stored = normalizeInstance(localStorage.getItem(Constants.LS_CURRENT_INSTANCE));
    const inferred = inferInstance();
    const instance = (stored ?? inferred) || '';

    if (inferred && stored !== inferred) {
      try {
        localStorage.setItem(Constants.LS_CURRENT_INSTANCE, inferred);
      } catch {
        // ignore
      }
    }

    const instancePath = instance ? `${this.prefix}instances/${instance}/${lang}${this.suffix}` : '';

    const base$ = this.http.get(basePath).pipe(catchError(() => of({})));
    const inst$ = instancePath ? this.http.get(instancePath).pipe(catchError(() => of({}))) : of({});

    return forkJoin([base$, inst$]).pipe(map(([base, inst]) => deepMerge(base, inst)));
  }
}

function deepMerge(target: any, source: any): any {
  if (!isPlainObject(target)) return source;
  if (!isPlainObject(source)) return target;

  const out: any = { ...target };
  for (const key of Object.keys(source)) {
    const srcVal = source[key];
    const tgtVal = out[key];
    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      out[key] = deepMerge(tgtVal, srcVal);
    } else {
      out[key] = srcVal;
    }
  }
  return out;
}

function isPlainObject(v: any): v is Record<string, any> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}
